/**
 * PicksResultsSection - Displays generated picks: warnings, scoring info, filter bar,
 * and risk-tier-grouped ParlayCards.
 */

import { useFeatureAccess } from '@hooks/useFeatureAccess';
import type { ParlayScore } from '@hooks/useStrategyStats';
import type { GeneratedParlay } from '@parlay-picker/shared/api/picksApi';
import type { BestOddsResult } from '@parlay-picker/shared/oddsUtils';
import { createParlayKey, type LegSignatureInput } from '@parlay-picker/shared/parlaySignature';
import { percentileRank } from '@parlay-picker/shared/percentile';
import { useOptimizerStore } from '@stores/optimizerStore';
import { AlertTriangle, ChevronDown, Sparkles } from 'lucide-react';
import { type ReactElement, useCallback, useMemo, useState } from 'react';
import type { PicksFilter } from '../constants';
import type { Game, RiskTierGroup } from '../utils';
import { uniqueOptimizerLegsFrom } from './optimizerTrack';
import type { ChartClickData } from './ParlayCard';
import { ParlayCard } from './ParlayCard';
import { PicksFilterBar } from './PicksFilterBar';

interface CalibrationThresholds {
  betThreshold: number;
  skipThreshold: number;
  confidenceLevel: 'high' | 'medium' | 'low';
  sampleCount: number;
}

interface InlineParlayScore {
  strategyScore: ParlayScore | null;
  predictionScore: ParlayScore | null;
}

interface StrategyStatsInfo {
  totalSettled: number;
  totalSettledLegs?: number;
}

interface PredictionStatsInfo {
  totalResolved: number;
}

interface CollapseState {
  collapsedTiers: Set<string>;
  collapsedParlays: Set<number>;
}

export interface PicksResultsSectionProps {
  // Parlay data
  recs: GeneratedParlay[];
  inlineScores: InlineParlayScore[];
  generationId: string | null;
  games: Game[];

  // Warnings & cache
  statsWarning: string | null;
  wasCached: boolean;
  lastFreshFetch: Date | null;
  sportsbookName: string;

  // Strategy stats
  strategyStats: StrategyStatsInfo | null;
  predictionStats: PredictionStatsInfo | null;
  calibration: {
    strategy: CalibrationThresholds | null;
    prediction: CalibrationThresholds | null;
  } | null;

  // Filters
  filters: PicksFilter;
  onFiltersChange: (filters: PicksFilter) => void;
  filteredRecs: GeneratedParlay[];
  groupedRecs: RiskTierGroup[];

  // Collapse state
  collapseState: CollapseState;
  onToggleTierCollapse: (tierName: string) => void;
  onToggleParlayCollapse: (rank: number) => void;

  // Parlay card props
  showDetailedAnalysis: boolean;
  oddsComparisons: Map<string, BestOddsResult>;
  onTrack: (rec: GeneratedParlay, tierName: string) => Promise<void>;
  isParlayTracked: (rec: GeneratedParlay) => boolean;
  trackingKeys: Set<string>;
  onChartClick: (data: ChartClickData) => void;
  onScoreRefresh: () => Promise<void>;
}

function formatRelativeTime(date: Date): string {
  const mins = Math.round((Date.now() - date.getTime()) / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  return `${hrs}h ${mins % 60}m ago`;
}

export function PicksResultsSection({
  recs,
  inlineScores,
  generationId,
  games,
  statsWarning,
  wasCached,
  lastFreshFetch,
  sportsbookName,
  strategyStats,
  predictionStats,
  calibration,
  filters,
  onFiltersChange,
  filteredRecs,
  groupedRecs,
  collapseState,
  onToggleTierCollapse,
  onToggleParlayCollapse,
  showDetailedAnalysis,
  oddsComparisons,
  onTrack,
  isParlayTracked,
  trackingKeys,
  onChartClick,
  onScoreRefresh,
}: PicksResultsSectionProps): ReactElement {
  const { isHidden } = useFeatureAccess();
  const openOptimizer = useOptimizerStore((s) => s.openWithLegs);
  const optimizerHidden = isHidden('ml_parlay_optimizer');

  // Extract unique legs from all generated parlays for optimizer
  const uniqueOptimizerLegs = useMemo(() => uniqueOptimizerLegsFrom(recs), [recs]);

  // Compute slate-relative strength percentiles from inline scores.
  // Uses the prediction model score (available on all generated parlays) as the
  // quality metric. Consumers see a "Top X%" badge on each ParlayCard.
  const strengthPercentiles = useMemo(() => {
    if (inlineScores.length < 3) return null;
    const scores = inlineScores.map((s) => s.predictionScore?.score ?? s.strategyScore?.score ?? 0);
    return scores.map((score) => percentileRank(score, scores));
  }, [inlineScores]);

  const getParlayTrackingKey = (rec: GeneratedParlay): string =>
    createParlayKey(
      (rec.legs ?? []).map(
        (leg): LegSignatureInput => ({
          player: leg.player || leg.team || '',
          statType: leg.statType || '',
          line: leg.line,
          overUnder: leg.overUnder,
          gameDate: leg.gameDate,
          leagueKey: leg.leagueKey,
        }),
      ),
    );
  // Accordion state: track which parlay's AI Analysis detail section is expanded (by rank)
  // Only one can be open at a time — toggling one closes the previously open one
  const [expandedDetailRank, setExpandedDetailRank] = useState<number | null>(null);
  const toggleDetail = useCallback(
    (rank: number) => setExpandedDetailRank((prev) => (prev === rank ? null : rank)),
    [],
  );

  return (
    <div className="space-y-4">
      {statsWarning && (
        <div className="bg-theme-warning/20 border border-theme-warning/50 rounded-lg p-2 text-center text-sm text-theme-warning flex items-center justify-center gap-2">
          <AlertTriangle className="w-4 h-4 shrink-0" />
          {statsWarning}
        </div>
      )}

      {wasCached && (
        <div className="bg-theme-success/20 border border-theme-success/50 rounded-lg p-2 text-center text-sm text-theme-success">
          No quota used — showing your recent picks
          {lastFreshFetch && (
            <span className="text-theme-text-tertiary ml-1">
              (generated {formatRelativeTime(lastFreshFetch)})
            </span>
          )}
        </div>
      )}

      {/* Strategy stats indicator */}
      {((strategyStats?.totalSettledLegs ?? strategyStats?.totalSettled ?? 0) > 0 ||
        (predictionStats?.totalResolved ?? 0) > 0) && (
        <div className="bg-gradient-to-r from-purple-500/30 to-theme-primary/20 border border-purple-700/50 rounded-lg p-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-theme-success animate-pulse" />
              <span className="text-sm font-medium text-purple-400">Live DB Scoring</span>
            </div>
            <div className="flex items-center gap-3 text-xs text-theme-text-secondary">
              {strategyStats &&
                (strategyStats.totalSettledLegs ?? strategyStats.totalSettled) > 0 && (
                  <span>
                    🎯 {strategyStats.totalSettledLegs ?? strategyStats.totalSettled} settled legs
                  </span>
                )}
              {predictionStats && predictionStats.totalResolved > 0 && (
                <span>
                  📊 {predictionStats.totalResolved.toLocaleString()} resolved predictions
                </span>
              )}
            </div>
          </div>
          <p className="text-xs text-theme-text-tertiary mt-1">
            📊 The AI score on every pick, and the personal score (You) once a user's tracked bets
            have proven an edge over the AI. Hover for details.
          </p>
          {calibration && (calibration.strategy || calibration.prediction) && (
            <p className="text-xs text-theme-text-tertiary mt-1">
              {calibration.strategy && calibration.strategy.confidenceLevel !== 'low' && (
                <span className="text-purple-400">
                  Thresholds calibrated from {calibration.strategy.sampleCount.toLocaleString()}{' '}
                  scored & settled parlays
                  {calibration.strategy.confidenceLevel === 'high'
                    ? ' (high confidence)'
                    : ' (medium confidence)'}
                </span>
              )}
              {calibration.strategy && calibration.strategy.confidenceLevel === 'low' && (
                <span className="text-theme-text-tertiary">
                  Calibrating... ({calibration.strategy.sampleCount}/200 scored & settled parlays
                  needed)
                </span>
              )}
            </p>
          )}
        </div>
      )}

      <PicksFilterBar
        filters={filters}
        onFiltersChange={onFiltersChange}
        totalCount={recs.length}
        filteredCount={filteredRecs.length}
      />

      {/* Optimize button — extract all unique legs and open optimizer modal */}
      {uniqueOptimizerLegs.length >= 4 && !optimizerHidden && (
        <button
          type="button"
          onClick={() => openOptimizer(uniqueOptimizerLegs)}
          className="w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg bg-purple-600/20 border border-purple-500/30 text-purple-300 text-sm font-medium hover:bg-purple-600/30 transition-colors"
        >
          <Sparkles className="w-4 h-4" />
          Optimize {uniqueOptimizerLegs.length} Legs
        </button>
      )}

      {groupedRecs.map((tier) => {
        if (tier.parlays.length === 0) return null;
        const isTierCollapsed = collapseState.collapsedTiers.has(tier.name);

        return (
          <div key={tier.name} className="space-y-3">
            {/* Tier header - clickable to collapse/expand */}
            <button
              type="button"
              onClick={() => onToggleTierCollapse(tier.name)}
              className={`${tier.bgColor} rounded-lg p-3 border-l-4 ${tier.color} w-full text-left hover:brightness-110 transition-all`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-bold text-lg text-theme-text">{tier.label}</h3>
                  <p className="text-theme-text-secondary text-sm">{tier.desc}</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-theme-text-secondary">
                    {tier.parlays.length} pick
                    {tier.parlays.length !== 1 ? 's' : ''}
                  </span>
                  <ChevronDown
                    className={`w-5 h-5 text-theme-text-secondary transition-transform ${isTierCollapsed ? '' : 'rotate-180'}`}
                  />
                </div>
              </div>
            </button>

            {/* Parlays - hidden when tier is collapsed */}
            {!isTierCollapsed &&
              tier.parlays.map((rec: GeneratedParlay, i: number) => {
                // Use inline scores from generation when available
                const originalIndex = recs.indexOf(rec);
                const inline = originalIndex >= 0 ? inlineScores[originalIndex] : undefined;

                // Use server-computed inline scores (dynamic caps + adaptive min-samples)
                const dynamicScore: ParlayScore | null = inline?.strategyScore ?? null;
                const predictionScore: ParlayScore | null = inline?.predictionScore ?? null;

                // Show unavailable state when we have picks but no scores
                const scoresUnavailable = recs.length > 0 && inlineScores.length === 0;

                const parlayRank = rec.rank ?? i;
                const isParlayCollapsedState = collapseState.collapsedParlays.has(parlayRank);

                return (
                  <ParlayCard
                    key={`${generationId}-${tier.name}-${parlayRank}`}
                    rec={rec}
                    index={i}
                    tier={tier}
                    leagueKey={rec.leagueKey}
                    games={games}
                    isTracked={isParlayTracked(rec)}
                    isTracking={trackingKeys.has(getParlayTrackingKey(rec))}
                    onTrack={onTrack}
                    onChartClick={onChartClick}
                    showDetailedAnalysis={showDetailedAnalysis}
                    oddsComparisons={oddsComparisons}
                    dynamicScore={dynamicScore}
                    predictionScore={predictionScore}
                    scoresUnavailable={scoresUnavailable}
                    onScoreRefresh={onScoreRefresh}
                    isCollapsed={isParlayCollapsedState}
                    onToggleCollapse={() => onToggleParlayCollapse(parlayRank)}
                    isDetailExpanded={expandedDetailRank === parlayRank}
                    onToggleDetail={() => toggleDetail(parlayRank)}
                    strengthPercentile={
                      originalIndex >= 0 ? strengthPercentiles?.[originalIndex] : undefined
                    }
                  />
                );
              })}
          </div>
        );
      })}

      <div className="bg-theme-warning/20 border border-theme-warning/50 rounded-lg p-3 text-sm text-theme-warning space-y-1">
        <p>
          Based on real {sportsbookName} lines. Odds may have moved - verify before placing bets.
          Gamble responsibly.
        </p>
        <p className="text-theme-warning/70 text-xs">
          Note: Stats include ESPN data for completed games from today/yesterday. In-progress games
          not included.
        </p>
      </div>
    </div>
  );
}
